import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useSocket } from '../contexts/SocketContext';
import { useToast } from './Toast';
import PuzzleUploadModal from './PuzzleUploadModal';
import PuzzleImagesMine from './PuzzleImagesMine';
import { copyToClipboard } from '../utils/clipboard';
import { buildXlsx, buildNumberText, shareFile } from '../utils/share';
import { filenameTimestamp } from '../utils/datetime';
import { PackageOpen, Package, Copy, Share2, ArrowRight, UploadCloud, CheckCircle2, ShieldAlert, AlertTriangle, Lock } from 'lucide-react';

// 工作台 · 证券侧：版式和排五一致（统计卡 → 当前任务；收益在「我的」页）。
// 只显示自己领取的包（一次可以领多个）；悬浮球上传三图。
// 悬浮球的外观、拖拽逻辑、位置存储（ws_fab_pos）与排五侧完全一致，两侧位置同步。
const FAB_SIZE = 56;
const FAB_KEY = 'ws_fab_pos';
const DRAG_THRESHOLD = 6;

function loadFabPos() {
  try {
    const s = localStorage.getItem(FAB_KEY);
    if (s) {
      const p = JSON.parse(s);
      if (typeof p.x === 'number' && typeof p.y === 'number') return p;
    }
  } catch {}
  return {
    x: (typeof window !== 'undefined' ? window.innerWidth : 375) - FAB_SIZE - 20,
    y: (typeof window !== 'undefined' ? window.innerHeight : 667) - FAB_SIZE - 80,
  };
}
function clampFab(x, y) {
  const pad = 4;
  return {
    x: Math.max(pad, Math.min(x, window.innerWidth - FAB_SIZE - pad)),
    y: Math.max(pad, Math.min(y, window.innerHeight - FAB_SIZE - pad)),
  };
}

export default function PuzzleWorkspace() {
  const navigate = useNavigate();
  const { subscribe } = useSocket();
  const { showToast } = useToast();

  const [data, setData] = useState(null);          // /puzzle/my-package 的返回（sessions = 进行中的全部包）
  const [stats, setStats] = useState(null);        // /puzzle/stats/mine 的返回（今日成功 / 待核实 / 收益）
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [clicked, setClicked] = useState(new Set());
  const [fabPos, setFabPos] = useState(loadFabPos);
  const fabDrag = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });

  const sessions = data?.sessions || [];
  const clickKey = sessions.length ? `puzzle_clicked_${sessions.map(x => x.id).join('_')}` : null;

  const fetchData = useCallback(async () => {
    try {
      const [pk, st] = await Promise.all([
        api.get('/puzzle/my-package'),
        api.get('/puzzle/stats/mine').catch(() => ({ data: null })),
      ]);
      setData(pk.data);
      if (st.data) setStats(st.data);
    } catch { /* 失败保留旧数据 */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (['puzzle_session_updated', 'puzzle_updated', 'puzzle_pool_updated', 'data_refresh'].includes(event)) fetchData();
    });
  }, [subscribe, fetchData]);

  // 已点击过的号码（换包后自动重新开始）
  useEffect(() => {
    if (!clickKey) { setClicked(new Set()); return; }
    try { setClicked(new Set(JSON.parse(localStorage.getItem(clickKey) || '[]'))); } catch { setClicked(new Set()); }
  }, [clickKey]);
  const markClicked = (id) => {
    if (!clickKey) return;
    setClicked(prev => {
      const next = new Set(prev); next.add(id);
      try { localStorage.setItem(clickKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const onResize = () => setFabPos(p => clampFab(p.x, p.y));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const hasPackage = sessions.length > 0;
  // 能不能上传：手上有进行中的包，或昨天/今天领过包（点了“完成本次”之后照样能继续上传；规则和排五一致，服务端同一个函数判断）。
  // data 还没加载出来时不拦，服务端会兜底校验。
  const canUpload = hasPackage || !data || !!data.can_upload;
  const uploadLocked = !!data && !canUpload;

  // ── 悬浮球 ─────────────────────────────────────────────
  const openUpload = () => {
    if (uploadLocked) { showToast('昨天和今天都没有领取数据包，请先到接量页领取', 'error'); return; }
    setUploadOpen(true);
  };
  const onFabPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    fabDrag.current = { active: false, startX: e.clientX, startY: e.clientY, originX: fabPos.x, originY: fabPos.y };
  };
  const onFabPointerMove = (e) => {
    const d = fabDrag.current;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (!d.active && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    d.active = true;
    setFabPos(clampFab(d.originX + dx, d.originY + dy));
  };
  const onFabPointerUp = (e) => {
    const d = fabDrag.current;
    if (!d.active) {
      openUpload();   // ← 与排五唯一的区别：排五这里是 openUploadFree()
    } else {
      const pos = clampFab(d.originX + e.clientX - d.startX, d.originY + e.clientY - d.startY);
      setFabPos(pos);
      try { localStorage.setItem(FAB_KEY, JSON.stringify(pos)); } catch {}
    }
    d.active = false;
  };

  // ── 号码操作（与排五号码列表一致）────────────────────────
  const handlePhoneTap = async (item) => {
    try { await copyToClipboard(item.phone); } catch {}
    showToast('号码已复制，正在跳转微信...', 'success');
    const a = document.createElement('a');
    a.href = 'weixin://';
    a.target = '_self';
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 0);
    markClicked(item.id);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(c => (c === item.id ? null : c)), 1500);
  };
  const handleCopyOne = async (item) => {
    await copyToClipboard(item.phone);
    showToast('已复制', 'success');
    markClicked(item.id);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(c => (c === item.id ? null : c)), 1500);
  };
  const numbers = data?.numbers || [];          // 全部包的号码（复制 / 转发用）
  const handleCopyAll = async () => {
    if (!numbers.length) return;
    await copyToClipboard(numbers.map(n => n.phone).join('\n'));
    showToast(`已复制全部 ${numbers.length} 个号码`, 'success');
  };
  const handleCopyText = async () => {
    if (!numbers.length) return;
    await copyToClipboard(buildNumberText(numbers.map(n => n.phone)));
    showToast(`已复制全部 ${numbers.length} 个号码（文本格式）`, 'success');
  };
  const handleShare = async () => {
    if (!numbers.length) return;
    const res = await shareFile({
      filename: `${sessions.length > 1 ? `${sessions[0].name}等${sessions.length}个包` : sessions[0].name}-${numbers.length}条-${filenameTimestamp()}.xlsx`,
      text: buildXlsx(numbers.map(n => ({ phone: n.phone }))),
    });
    if (res === 'shared') showToast('已生成表格文档，请选择微信/QQ等发送', 'success');
    else if (res === 'download') showToast('当前浏览器不支持弹分享面板，已改为直接下载表格文件', 'info');
  };

  const handleFinish = async () => {
    setFinishing(true);
    try {
      await api.post('/puzzle/session/complete', {});
      showToast('本次已完成，可回接量页领取新的数据包', 'success');
      setFinishOpen(false);
      await fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    } finally { setFinishing(false); }
  };

  if (loading) {
    return <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>;
  }

  const doneCount = numbers.filter(n => n.status !== 'todo').length;
  const todaySuccess = data?.today_success ?? stats?.counted_today ?? 0;   // 今日成功单数：按人按天累计，不分是哪个包的

  return (
    <>
      {/* 统计卡（和排五一致：已领 / 剩余 / 今日） */}
      <div className="card" style={{ display: 'flex', gap: 0 }}>
        <div className="stat-card" style={{ flex: 1 }}>
          <div className="stat-value" style={{ color: 'var(--c-primary)' }}>{data?.today_claimed ?? sessions.length}</div>
          <div className="stat-label">已领包数</div>
        </div>
        <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
        <div className="stat-card" style={{ flex: 1 }}>
          <div className="stat-value" style={{ color: 'var(--c-success)' }}>{data?.pool_count ?? 0}</div>
          <div className="stat-label">剩余可领</div>
        </div>
        <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
        <div className="stat-card" style={{ flex: 1 }}>
          <div className="stat-value" style={{ color: 'var(--c-accent)' }}>{todaySuccess}</div>
          <div className="stat-label">今日成功</div>
        </div>
      </div>

      {!hasPackage ? (
        /* 没有进行中的任务：和排五的空状态用同一句话，领取入口只在“接量”页，这里不再重复展示池子和领取说明 */
        <div className="card empty-state" style={{ padding: '40px 20px' }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
            <PackageOpen size={48} color="var(--c-text-secondary)" strokeWidth={1.2} />
          </div>
          <p style={{ fontWeight: 600, fontSize: 16, color: 'var(--c-text)' }}>当前无进行中的任务</p>
          <p style={{ fontSize: 13, marginTop: 6 }}>前往「接量」页面领取数据包</p>
          {data?.can_upload && (
            <p style={{ fontSize: 12, marginTop: 8, color: 'var(--c-success)' }}>
              {data.today_claimed > 0 ? '今天领过包，完成后仍可继续上传' : '昨天领过包，今天仍可继续补传'}
            </p>
          )}
          <button className="btn btn-primary" onClick={() => navigate('/request')} style={{ maxWidth: 200, margin: '20px auto 0' }}>
            去接量 <ArrowRight size={16} />
          </button>
        </div>
      ) : (
        <>
          <div className="card" style={{ background: 'linear-gradient(135deg, rgba(0,122,255,0.08), rgba(175,82,222,0.06))' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Package size={18} color="var(--c-primary)" />
              <span style={{ fontSize: 16, fontWeight: 800, flex: 1, wordBreak: 'break-all' }}>
                {sessions.length > 1 ? `本次领取 ${sessions.length} 个数据包` : sessions[0].name}
              </span>
              <span className="badge badge-info">进行中</span>
            </div>
            <div style={{ display: 'flex', gap: 0 }}>
              <div className="stat-card" style={{ flex: 1 }}>
                <div className="stat-value" style={{ color: 'var(--c-success)', fontSize: 24 }}>{doneCount}<span style={{ fontSize: 14, color: 'var(--c-text-secondary)' }}> / {numbers.length}</span></div>
                <div className="stat-label">已提交</div>
              </div>
              <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '8px 0' }} />
              <div className="stat-card" style={{ flex: 1 }}>
                <div className="stat-value" style={{ color: 'var(--c-primary)', fontSize: 24 }}>{todaySuccess}</div>
                <div className="stat-label">今日成功单数</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', lineHeight: 1.6, marginTop: 8 }}>
              这是您领取的数据包，仅您本人可见。按列表逐条联系客户，添加成功后用悬浮球上传三张图；联系不上的号码可跳过。全部处理完点下方“完成本次”。
            </div>
          </div>

          <div className="card">
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button onClick={handleCopyAll} className="btn btn-primary" style={{ flex: 1, height: 42, fontSize: 14 }}>
                <Copy size={15} /> 一键复制全部 {numbers.length} 个号码
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button onClick={handleShare} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 0',
                background: 'var(--c-success)', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
              }}><Share2 size={15} /> 转发文档</button>
              <button onClick={handleCopyText} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 0',
                background: 'var(--c-bg)', color: 'var(--c-text)', border: '1px solid var(--c-border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}><Copy size={15} /> 复制文本</button>
            </div>

            {sessions.map((sess) => (
              <div key={sess.id}>
                {sessions.length > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '12px 0 2px', padding: '7px 10px', background: 'var(--c-bg)', borderRadius: 8 }}>
                    <Package size={14} color="var(--c-primary)" style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 700, wordBreak: 'break-all' }}>{sess.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--c-text-secondary)', flexShrink: 0 }}>已提交 {sess.done} / {sess.total}</span>
                  </div>
                )}
                {(sess.numbers || []).map((item, i) => {
                  const isClicked = clicked.has(item.id);
                  return (
                    <div key={item.id} style={{ padding: '11px 0', borderBottom: '0.5px solid var(--c-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 12, color: 'var(--c-text-tertiary)', minWidth: 28 }}>#{i + 1}</span>
                      <span onClick={() => handlePhoneTap(item)} style={{
                        flex: 1, fontSize: 16, fontWeight: 600, letterSpacing: '0.5px', cursor: 'pointer',
                        color: item.status !== 'todo' || isClicked ? 'var(--c-text-tertiary)' : 'var(--c-primary)',
                        textDecoration: item.status === 'done' ? 'line-through' : 'none',
                        opacity: item.status !== 'todo' || isClicked ? 0.6 : 1, transition: 'color .2s, opacity .2s',
                      }}>{item.phone}</span>
                      {item.status === 'done' && (
                        <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <CheckCircle2 size={11} /> 完成
                        </span>
                      )}
                      {item.status === 'pending' && (
                        <span className="badge badge-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <ShieldAlert size={11} /> 待核实
                        </span>
                      )}
                      <button onClick={() => handleCopyOne(item)} style={{
                        background: copiedId === item.id ? 'rgba(52,199,89,0.1)' : 'transparent',
                        border: `1px solid ${copiedId === item.id ? 'rgba(52,199,89,0.4)' : 'var(--c-border)'}`,
                        borderRadius: 8, padding: '5px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13,
                        color: copiedId === item.id ? 'var(--c-success)' : 'var(--c-text-secondary)', flexShrink: 0,
                      }}>
                        <Copy size={13} /> {copiedId === item.id ? '已复制' : '复制'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}

            <button className="btn btn-accent" onClick={() => setFinishOpen(true)} style={{ marginTop: 16, height: 46 }}>
              完成本次
            </button>
          </div>
        </>
      )}

      {/* 图片管理（始终显示，不依赖是否有任务；和排五工作台底部的“图片管理”同位置） */}
      <PuzzleImagesMine refreshKey={`${todaySuccess}_${stats?.pending_today ?? 0}_${stats?.rejected_today ?? 0}`} />

      {/* 上传三图：悬浮球（可拖动，与排五同外观） */}
      <button
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={onFabPointerUp}
        style={{
          position: 'fixed', left: fabPos.x, top: fabPos.y, zIndex: 50,
          width: FAB_SIZE, height: FAB_SIZE, borderRadius: '50%',
          background: uploadLocked ? 'linear-gradient(135deg, #8e8e93 0%, #636366 100%)' : 'linear-gradient(135deg, #0A84FF 0%, #0055D4 100%)',
          border: 'none', cursor: 'grab', touchAction: 'none', userSelect: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 1,
          boxShadow: uploadLocked ? '0 4px 16px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.10)' : '0 4px 16px rgba(0,122,255,0.45), 0 1px 4px rgba(0,0,0,0.15)',
          opacity: uploadLocked ? 0.85 : 1,
        }}
        title={uploadLocked ? '昨天和今天都没有领取数据包，领取后才能上传' : '上传三张图片（可拖动调整位置）'}
      >
        {uploadLocked
          ? <>
              <Lock size={18} color="white" strokeWidth={2.5} />
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.85)', lineHeight: 1, marginTop: 1 }}>需领包</span>
            </>
          : <UploadCloud size={24} color="white" strokeWidth={2} />}
      </button>

      {uploadOpen && (
        <PuzzleUploadModal
          onClose={() => { setUploadOpen(false); fetchData(); }}
          onChanged={fetchData}
          onFinish={() => setFinishOpen(true)}
        />
      )}

      {finishOpen && createPortal(
        <div onClick={() => !finishing && setFinishOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '0 24px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--c-surface)', borderRadius: 18, padding: '24px 20px 20px', width: '100%', maxWidth: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <AlertTriangle size={20} color="var(--c-accent)" />
              <span style={{ fontSize: 16, fontWeight: 700 }}>请确认后再提交</span>
            </div>
            <div style={{ fontSize: 14, color: 'var(--c-text-secondary)', lineHeight: 1.7, marginBottom: 20 }}>
              请勿漏加号码，<strong style={{ color: 'var(--c-danger)' }}>漏加少加影响结算</strong>。中途出现异常情况请联系上级处理剩余数据。
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setFinishOpen(false)} disabled={finishing} style={{
                flex: 1, height: 44, borderRadius: 12, fontSize: 15, fontWeight: 600, background: 'var(--c-bg)', color: 'var(--c-text)', border: '1px solid var(--c-border)', cursor: 'pointer',
              }}>继续添加</button>
              <button onClick={handleFinish} disabled={finishing} style={{
                flex: 1, height: 44, borderRadius: 12, fontSize: 15, fontWeight: 600, background: 'var(--c-primary)', color: '#fff', border: 'none', cursor: 'pointer', opacity: finishing ? 0.6 : 1,
              }}>{finishing ? '处理中…' : '确认完成本次'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
